// OpenTelemetry web SDK for the storefront (epic #183, ADR 0036) — the same
// mechanism admin-ui uses (#126, ADR 0031; originally load-generator-ui's,
// retired in #262 / ADR 0064), one of two browser origins left for it.
//
// The point: "Place order" clicked in the browser starts a span here, the
// fetch to the load-generator carries W3C `traceparent` automatically, and
// — because checkout always sends `count: 1` — the load-generator
// propagates that context into order-service (run.go: `propagate := Count
// == 1`). So one trace runs from the checkout click through all five
// backend languages: browse → cart → checkout, one waterfall.
//
// Vendor-neutral, like the rest of the repo: OTLP/HTTP exporter + the plain
// OTel API only. The browser can't hold an ingest key — since #248 the app
// is same-origin behind the BFF, so spans export to a relative
// `/v1/traces`, which the BFF forwards to the load-generator's OTLP
// receiver (services/load-generator/traces.go). `traceparent` is injected
// on same-origin fetches by default.

import { type Attributes, type Span, SpanStatusCode, trace } from "@opentelemetry/api";
import { ZoneContextManager } from "@opentelemetry/context-zone";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { BatchSpanProcessor, WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import { ATTR_SERVICE_NAME } from "@opentelemetry/semantic-conventions";

export const TRACER_NAME = "storefront-ui";

let started = false;

/**
 * Wire up the web SDK. Call once, before the first render. Idempotent.
 * Exports to a relative `/v1/traces` (the BFF forwards it); in local dev
 * with no BFF that just 404s and spans are dropped — harmless.
 */
export function initBrowserOtel(namespace?: string): void {
  if (started) return;
  started = true;

  const attributes: Attributes = { [ATTR_SERVICE_NAME]: TRACER_NAME };
  if (namespace) attributes["service.namespace"] = namespace;

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes(attributes),
    spanProcessors: [
      new BatchSpanProcessor(new OTLPTraceExporter({ url: "/v1/traces" })),
    ],
  });
  provider.register({ contextManager: new ZoneContextManager() });

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        // Same-origin now — the default already injects `traceparent` on
        // same-origin fetches, so no propagateTraceHeaderCorsUrls needed.
        // Only the checkout POST is worth a span: the OTLP export would
        // recurse, the status polls would be orphan roots, and the
        // catalog/customer fetches are noise.
        ignoreUrls: [
          /\/v1\/traces(\?|$)/,
          /\/api\/orders(\/|\?|$)/,
          /\/api\/products(\/|\?|$)/,
          /\/auth\//,
          /\/(products|customers|config)\.json(\?|$)/,
        ],
        clearTimingResources: true,
      }),
    ],
  });
}

/**
 * Run `fn` inside a span named `name`, passing it the span so it can add
 * attributes it only learns mid-flight (e.g. the returned orderId). If the
 * SDK never started (local dev) this is just `fn(noopSpan)` — the OTel API
 * hands back a no-op tracer.
 */
export async function withSpan<T>(
  name: string,
  attributes: Attributes,
  fn: (span: Span) => Promise<T>,
): Promise<T> {
  const tracer = trace.getTracer(TRACER_NAME);
  return tracer.startActiveSpan(name, { attributes }, async (span) => {
    try {
      return await fn(span);
    } catch (err) {
      span.recordException(err as Error);
      span.setStatus({ code: SpanStatusCode.ERROR });
      throw err;
    } finally {
      span.end();
    }
  });
}
